// src/components/CosmicBlueprint.js
import React, { useState } from 'react';
import axios from 'axios';

const TOPICS = [
  'Love & Relationships',
  'Career & Success',
  'Money & Finance',
  'Wellbeing & Health',
  'Spiritual Growth',
  'Family & Home',
];

const CosmicBlueprint = ({ user, products, blueprintPriceId, onBlueprintPurchased }) => {
  const [giftMode, setGiftMode] = useState(false);
  const [blueprintType, setBlueprintType] = useState("year"); // 👈 NEW
  const [giftDetails, setGiftDetails] = useState({
    recipientName: '',
    recipientEmail: '',
    topics: [],
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const toggleTopic = (topic) => {
    setGiftDetails((prev) => ({
      ...prev,
      topics: prev.topics.includes(topic)
        ? prev.topics.filter((t) => t !== topic)
        : [...prev.topics, topic],
    }));
  };

  const createCheckoutSession = async (isGift) => {
    setErr('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const payload = {
        priceId: blueprintPriceId,
        customerEmail: user.email,
        blueprintType, // 👈 NEW
        gift: isGift
          ? {
              isGift: true,
              recipientName: giftDetails.recipientName,
              recipientEmail: giftDetails.recipientEmail,
              recipientTopics: giftDetails.topics,
              message: giftDetails.message,
            }
          : null,
      };

      const res = await axios.post(
        '/api/payments/create-checkout-session',
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      window.location.href = res.data.url;
    } catch (e) {
      console.error('Blueprint checkout error:', e);
      setErr('Failed to initiate purchase. Please try again.');
      setLoading(false);
    }
  };

  const priceLabel = blueprintType === "month" ? "£12" : "£29";
  const productLabel =
    blueprintType === "month"
      ? "30-Day Cosmic Blueprint"
      : "12-Month Cosmic Blueprint";

  return (
    <div className="bg-gradient-to-br from-purple-900 to-indigo-800 rounded-2xl p-6 shadow-lg text-white">
      <h3 className="text-xl font-bold mb-2">Cosmic Blueprint</h3>
      <p className="mb-2">
        Deep, personalised PDF natal report with love, career, and life guidance.
      </p>

      {err && (
        <div className="mb-2 text-red-300 bg-red-900 bg-opacity-30 px-3 py-2 rounded">
          {err}
        </div>
      )}

      {/* Blueprint Type Selector */}
      <div className="bg-black bg-opacity-30 rounded-xl p-3 mb-3 space-y-2">
        <div className="font-semibold text-sm mb-1">Choose your Blueprint:</div>
        <label className="flex items-center space-x-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="blueprintType"
            value="month"
            checked={blueprintType === "month"}
            onChange={() => setBlueprintType("month")}
            className="accent-pink-400"
          />
          <span>30-Day Blueprint (focused short-term guidance)</span>
        </label>
        <label className="flex items-center space-x-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="blueprintType"
            value="year"
            checked={blueprintType === "year"}
            onChange={() => setBlueprintType("year")}
            className="accent-pink-400"
          />
          <span>12-Month Blueprint (deep year-ahead life reading)</span>
        </label>
      </div>

      <div className="flex flex-col gap-3">
        {/* Buy for self */}
        <button
          onClick={() => createCheckoutSession(false)}
          disabled={loading || products.includes('blueprint')}
          className="font-bold py-2 px-4 rounded-xl bg-purple-200 hover:bg-purple-300 text-black flex justify-center"
        >
          {products.includes('blueprint')
            ? '✓ Purchased'
            : `Buy ${productLabel} – ${priceLabel}`}
        </button>

        {/* Gift toggle */}
        <button
          onClick={() => {
            setGiftMode((v) => !v);
            setErr('');
          }}
          type="button"
          className="text-sm underline self-start"
        >
          {giftMode ? 'Cancel gifting' : 'Gift this report to a friend'}
        </button>

        {giftMode && (
          <div className="bg-black bg-opacity-30 rounded-xl p-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Friend’s Name"
                className="flex-1 bg-black bg-opacity-20 text-white px-3 py-2 rounded"
                value={giftDetails.recipientName}
                onChange={(e) =>
                  setGiftDetails((prev) => ({ ...prev, recipientName: e.target.value }))
                }
                disabled={loading}
              />
              <input
                type="email"
                placeholder="Friend’s Email"
                className="flex-1 bg-black bg-opacity-20 text-white px-3 py-2 rounded"
                value={giftDetails.recipientEmail}
                onChange={(e) =>
                  setGiftDetails((prev) => ({ ...prev, recipientEmail: e.target.value }))
                }
                disabled={loading}
              />
            </div>

            <div>
              <div className="font-semibold mb-1">Topics for them:</div>
              <div className="flex flex-wrap gap-2">
                {TOPICS.map((topic) => (
                  <label key={topic} className="flex items-center space-x-1 text-sm">
                    <input
                      type="checkbox"
                      className="form-checkbox accent-pink-400"
                      checked={giftDetails.topics.includes(topic)}
                      onChange={() => toggleTopic(topic)}
                      disabled={loading}
                    />
                    <span>{topic}</span>
                  </label>
                ))}
              </div>
            </div>

            <textarea
              placeholder="Optional personal message"
              className="w-full bg-black bg-opacity-20 text-white px-3 py-2 rounded resize-none"
              rows={3}
              value={giftDetails.message}
              onChange={(e) =>
                setGiftDetails((prev) => ({ ...prev, message: e.target.value }))
              }
              disabled={loading}
            />

            <button
              onClick={() => createCheckoutSession(true)}
              disabled={
                loading ||
                !giftDetails.recipientName ||
                !giftDetails.recipientEmail
              }
              className="font-bold py-2 px-4 rounded-xl bg-pink-500 hover:bg-pink-600 text-white w-full"
            >
              {loading ? 'Processing…' : `Send ${productLabel} as Gift – ${priceLabel}`}
            </button>
          </div>
        )}
      </div>

      <p className="text-xs mt-3 opacity-80">
        After purchase, the Cosmic Blueprint is generated and emailed.  
        If gifted, your friend receives a beautiful redemption email to enter their birth details.
      </p>
    </div>
  );
};

export default CosmicBlueprint;
